import { useState, useCallback, useEffect } from 'react';
import { teamsDetailService } from '../services/teamsDetail.service';
import { useAuth } from '@/core/auth/AuthContext';
import { useToast } from '@/components/ui';

export function useTeamMembers(teamId: string) {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const { toast } = useToast();
  
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isMutating, setIsMutating] = useState(false);

  const fetchAvailableUsers = useCallback(async () => {
    if (!companyId || !teamId) return;
    setIsLoadingUsers(true);
    try {
      const users = await teamsDetailService.getAvailableUsers(companyId, teamId);
      setAvailableUsers(users);
    } catch (error) {
      console.error('Lỗi tải danh sách người dùng:', error);
    } finally {
      setIsLoadingUsers(false);
    }
  }, [companyId, teamId]);

  useEffect(() => {
    fetchAvailableUsers();
  }, [fetchAvailableUsers]);

  const addMember = async (userIds: string[], role: string, onSuccess?: () => void) => {
    if (!userIds || userIds.length === 0) return;
    setIsMutating(true);
    try {
      await teamsDetailService.addMembers(teamId, userIds, role);
      toast.success(`Đã thêm ${userIds.length} thành viên vào team`);
      await fetchAvailableUsers();
      onSuccess?.();
    } catch (error: any) {
      toast.error(error.message || 'Không thể thêm thành viên');
    } finally {
      setIsMutating(false);
    }
  };

  const removeMember = async (memberId: string, onSuccess?: () => void) => {
    if (!memberId) return;
    setIsMutating(true);
    try {
      await teamsDetailService.removeMember(memberId);
      toast.success('Đã xóa thành viên khỏi team');
      await fetchAvailableUsers();
      onSuccess?.();
    } catch (error: any) {
      toast.error(error.message || 'Không thể xóa thành viên');
    } finally {
      setIsMutating(false);
    }
  };

  const updateRole = async (memberId: string, role: string, onSuccess?: () => void) => {
    if (!memberId || !role) return;
    setIsMutating(true);
    try {
      await teamsDetailService.updateMemberRole(memberId, role);
      toast.success('Đã cập nhật quyền hạn');
      onSuccess?.();
    } catch (error: any) {
      toast.error(error.message || 'Không thể cập nhật quyền hạn');
    } finally {
      setIsMutating(false);
    }
  };

  return {
    availableUsers,
    isLoadingUsers,
    isMutating,
    fetchAvailableUsers,
    addMember,
    removeMember,
    updateRole
  };
}
